/**
 * nav-link-discovery.js — which anchors in a navigation are its LINKS (BC-3, F5).
 *
 * The shared navigation's link editor found links with `.cs-li-link` and
 * `.cs-drop-link` only. Those are CodeStitch class names; an imported template
 * (Webflow, Framer, a hand-written site) has none of them, so its navigation
 * opened an editor with an empty "Nav Links" list and no explanation.
 *
 * Discovery is structural, with CodeStitch as a fast path:
 *
 *  1. If the markup uses CodeStitch's link classes, trust them exactly — the
 *     result for every existing CodeStitch navigation is unchanged.
 *  2. Otherwise take the anchors inside the navigation landmark (`nav`,
 *     `[role="navigation"]`, else the section root), and drop the ones that are
 *     not destinations a visitor reads as "the menu": the logo, icon-only
 *     links (social glyphs), `tel:` / `mailto:` contact links, and in-page
 *     skip links.
 *  3. A link nested one list deeper than the menu's top level (`li li a`), or
 *     inside a dropdown/submenu container, is a dropdown link.
 *
 * Templates very often render the menu TWICE (a desktop bar and a mobile
 * drawer). Editing the first copy alone would leave the two disagreeing, so
 * links are GROUPED by their visible label and href, and an edit applies to
 * every anchor in its group.
 *
 * Deterministic over the same HTML: the editor renders from one parse and
 * writes back into a fresh parse by group index, and both see the same groups.
 */

// Not `[class*="drop"]`: that also matches `backdrop` and `drop-shadow`, and a
// nav backdrop wrapping the whole menu would turn every link into a dropdown.
const DROPDOWN_CONTAINER =
  '[class*="dropdown" i], [class*="drop-down" i], [class*="submenu" i], [class*="sub-menu" i]';
const LOGO_HINT = /(^|[\s_-])logo([\s_-]|$)|brand/i;

function linkText(a) {
  return (a.textContent || '').replace(/\s+/g, ' ').trim();
}

function isLogoLink(a) {
  const cls = `${a.getAttribute('class') || ''} ${a.getAttribute('id') || ''}`;
  if (LOGO_HINT.test(cls)) return true;
  // A link whose only visible content is an image is a logo or an icon.
  return !linkText(a) && !!a.querySelector('img, svg, picture');
}

function isNonDestination(a) {
  const href = (a.getAttribute('href') || '').trim().toLowerCase();
  if (href.startsWith('tel:') || href.startsWith('mailto:')) return true;
  if (href.startsWith('javascript:')) return true;
  // An in-page skip link ("Skip to content") is accessibility plumbing, not a
  // menu destination.
  if (href.startsWith('#') && /\bskip\b/i.test(linkText(a))) return true;
  return false;
}

function isDropdownLink(a, scope) {
  let listDepth = 0;
  for (let el = a.parentElement; el && el !== scope; el = el.parentElement) {
    if (el.tagName === 'LI') listDepth += 1;
    if (el.tagName !== 'LI' && el.matches?.(DROPDOWN_CONTAINER)) return true;
  }
  return listDepth > 1;
}

function group(anchors) {
  const groups = [];
  const byKey = new Map();
  for (const a of anchors) {
    const key = `${linkText(a)}\u0000${a.getAttribute('href') || ''}`;
    let entry = byKey.get(key);
    if (!entry) {
      entry = { label: linkText(a), href: a.getAttribute('href') || '', links: [] };
      byKey.set(key, entry);
      groups.push(entry);
    }
    entry.links.push(a);
  }
  return groups;
}

/**
 * @param {Element} root — a parsed wrapper around the navigation section's HTML
 * @returns {{strategy: 'codestitch'|'structural', top: LinkGroup[], drop: LinkGroup[]}}
 *   where LinkGroup is `{label: string, href: string, links: Element[]}`
 */
export function discoverNavLinks(root) {
  if (!root) return { strategy: 'structural', top: [], drop: [] };

  const codestitch = Array.from(root.querySelectorAll('.cs-li-link'));
  if (codestitch.length) {
    return {
      strategy: 'codestitch',
      top: group(codestitch.filter((a) => !a.classList.contains('cs-drop-link'))),
      drop: group(codestitch.filter((a) => a.classList.contains('cs-drop-link')))
    };
  }

  const landmarks = Array.from(root.querySelectorAll('nav, [role="navigation"]'));
  const scopes = landmarks.length ? landmarks : [root];
  const seen = new Set();
  const top = [];
  const drop = [];
  for (const scope of scopes) {
    for (const a of scope.querySelectorAll('a[href]')) {
      if (seen.has(a)) continue;
      seen.add(a);
      if (!linkText(a) || isLogoLink(a) || isNonDestination(a)) continue;
      (isDropdownLink(a, scope) ? drop : top).push(a);
    }
  }
  return { strategy: 'structural', top: group(top), drop: group(drop) };
}
